import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Card } from 'components/card';
import { Button } from 'components/buttons';
import { Banner } from 'components/banner';
import { AlertCircle } from 'components/icons/alert-circle';
import {
  useCreateSuitabilityTest,
  usePreviewSuitability,
  useQuestionnaireContent,
  type CreateSuitabilityTestResponse,
  type PreviewSuitabilityResponse,
} from 'queries/suitability';
import { useClient } from 'queries/clients';
import { useEmbedAuth } from 'contexts/embed-auth-context';
import {
  notifyTestCompleted,
  getAccessToken,
  waitForAccessToken,
} from 'libs/embed-auth';
import { blobToBase64 } from 'libs/blob';
import { globalConfig } from 'src/configuration/config';
import { resolveCurrentTest } from '../tests/registry';
import type { SuitabilityTestDefinition, TestContext } from './types';
import {
  injectQuestionnaireContent,
  stepLabelKey,
  type InjectableContent,
} from './i18n-questionnaire';
import { buildPdfDetail } from './pdf-payload';
import { useWizard } from './hooks/use-wizard';
import { WizardProgress } from './components/wizard-progress';
import { ReviewStep } from './components/review-step';
import { ResultStep } from './components/result-step';

/** Answers son un bag genérico a nivel de motor; cada test las tipa internamente. */
type Answers = Record<string, unknown>;

interface WizardRunnerProps {
  test: SuitabilityTestDefinition<Answers>;
  clientId: string;
  /** Versión del cuestionario resuelta (la que devuelve el EP de contenido). */
  version: string;
  /** Idioma con el que se pintó el test; se reenvía a wio.pdf para el PDF. */
  language: string;
}

/** El wizard en sí. Se monta sólo cuando ya conocemos el test, el token y el contenido. */
const WizardRunner = ({
  test,
  clientId,
  version,
  language,
}: WizardRunnerProps) => {
  const { t } = useTranslation();
  const wizard = useWizard(test.steps.length + 1); // +1 = paso "Resultados"
  const [answers, setAnswers] = useState<Answers>(test.initialAnswers);
  const [result, setResult] = useState<CreateSuitabilityTestResponse | null>(
    null,
  );
  const createMutation = useCreateSuitabilityTest(clientId);
  const preview = usePreviewSuitability();
  const contentRef = useRef<HTMLDivElement>(null);
  const { data: client } = useClient(clientId);
  const context = useMemo<TestContext>(
    () => ({ personType: client?.personType ?? undefined }),
    [client],
  );
  // PDF del test generado desde el preview: se muestra en el review y se adjunta al
  // Create. `blob` es lo que se envía; `url` es el object URL para el <iframe>.
  const [pdf, setPdf] = useState<{ blob: Blob; url: string } | null>(null);
  const [pdfState, setPdfState] = useState<'idle' | 'loading' | 'error'>('idle');
  const pdfUrlRef = useRef<string | null>(null);

  const isReviewStep = wizard.index === test.steps.length;

  // Al cambiar de paso, reinicia el scroll del contenido al inicio.
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [wizard.index]);

  const onUpdate = (patch: Partial<Answers>) =>
    setAnswers((prev) => ({ ...prev, ...patch }));

  // (Re)lanza el preview INVALIDANDO el PDF anterior: así, tras editar respuestas y
  // volver al review, Firmar no puede enviar un PDF que ya no corresponde a las
  // answers actuales. El PDF se regenera cuando llega el nuevo resultado.
  const { mutate: runPreview } = preview;
  const runFreshPreview = () => {
    setPdf(null);
    setPdfState('idle');
    runPreview({ answers, questionnaireVersion: version });
  };

  // Al entrar en el review, (re)calcula el resultado sin persistir: pinta el perfil
  // y sirve de base para generar el PDF. Determinista (mismas answers → mismo result).
  useEffect(() => {
    if (isReviewStep) runFreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewStep]);

  /**
   * Monta el payload de wio.pdf desde el resultado del preview y devuelve el Blob.
   * De ahí salen el `result`, el `externalRiskProfileId` (medidor de riesgo) y el
   * `validUntil`. wio.pdf exige el bearer del asesor (verja de auth) y saca el
   * companyId del token; el bearer dispara preflight CORS, que wio.pdf maneja.
   */
  const generatePdf = async (
    previewData: PreviewSuitabilityResponse,
  ): Promise<Blob> => {
    // Nombre del cliente para el PDF: name + lastName de wio.api (ambos pueden ser
    // null); filtramos para no meter "undefined" ni espacios sobrantes.
    const clientName = [client?.name, client?.lastName]
      .filter(Boolean)
      .join(' ');
    const detail = buildPdfDetail({
      clientId,
      clientName,
      questionnaireVersion: version,
      language,
      createdAt: new Date().toISOString(),
      validUntil: previewData.validUntil,
      riskProfileId: previewData.externalRiskProfileId,
      answers,
      result: previewData.result,
    });
    // El asesor no tiene sesión propia: el padre relaya su token (embed-auth).
    const token = getAccessToken() ?? (await waitForAccessToken());
    const response = await fetch(
      `${globalConfig.config.wioPdfUrl}/suitability-test`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${token}`,
        },
        body: JSON.stringify(detail),
      },
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  };

  // Genera el PDF cuando hay resultado de preview y cuando llega el nombre del
  // cliente (dep `client`, para no congelar un PDF con el nombre en blanco si
  // /clients aún no resolvió). Cada resultado nuevo regenera; el object URL
  // anterior se revoca al reemplazarlo. NO dependemos de `isReviewStep`: al volver
  // al review, `runPreview` limpia y reasigna `preview.data`, y esa nueva
  // referencia es la que dispara la (re)generación, evitando un render con datos
  // viejos y una llamada a wio.pdf desperdiciada.
  const previewData = preview.data;
  useEffect(() => {
    if (!isReviewStep || !previewData) return;
    let active = true;
    setPdfState('loading');
    generatePdf(previewData)
      .then((blob) => {
        if (!active) return;
        const url = URL.createObjectURL(blob);
        if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = url;
        setPdf({ blob, url });
        setPdfState('idle');
      })
      .catch(() => {
        if (active) setPdfState('error');
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewData, client]);

  // Revoca el último object URL al desmontar.
  useEffect(
    () => () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    },
    [],
  );

  // Reintenta desde el preview (invalidando el PDF anterior): sirve tanto si falló
  // el preview como si falló la generación del PDF.
  const handleRetry = runFreshPreview;

  if (result) {
    return (
      <Stack gap={3} sx={{ height: '100%', overflowY: 'auto', p: 3 }}>
        <ResultStep result={result} />
        <Stack direction="row" justifyContent="center">
          <Button
            variant="contained"
            onClick={() =>
              notifyTestCompleted(clientId, result.externalRiskProfileId)
            }
          >
            {t('wizard.nav.finish')}
          </Button>
        </Stack>
      </Stack>
    );
  }

  const currentStep = isReviewStep ? undefined : test.steps[wizard.index];
  const StepComponent = currentStep?.Component;
  // La etiqueta de cada paso sale del catálogo del API, que es donde vive; sin ella se
  // enseña la clave, para que el hueco se VEA en vez de dejar el stepper mudo.
  const labels = [
    ...test.steps.map((step) =>
      t(stepLabelKey(step.stepKey), { defaultValue: step.stepKey }),
    ),
    t('review.title'),
  ];

  const canAdvance =
    isReviewStep || (!!currentStep && currentStep.isComplete(answers, context));

  const handleSubmit = async () => {
    if (!pdf) return;
    try {
      const created = await createMutation.mutateAsync({
        answers,
        questionnaireVersion: version,
        pdf: await blobToBase64(pdf.blob),
      });
      // NO avisamos aún al padre: notificar cierra el diálogo en wio.front y no
      // se vería el resultado. Avisamos en "Finalizar".
      setResult(created);
    } catch {
      // Surfaced to the user via createMutation.isError below.
    }
  };

  return (
    <Card
      variant="elevation"
      elevation={0}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <CardHeader
        sx={{ flexShrink: 0 }}
        title={
          <Stack gap={3}>
            <Stack gap={0.5}>
              <Typography
                variant="textXs"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {t('wizard.progressTitle')}
              </Typography>
            </Stack>
            <WizardProgress
              current={wizard.index + 1}
              total={wizard.total}
              label={labels[wizard.index]}
            />
          </Stack>
        }
      />
      <Divider sx={{ flexShrink: 0 }} />
      <CardContent ref={contentRef} sx={{ flex: 1, overflowY: 'auto' }}>
        {StepComponent && (
          <StepComponent
            answers={answers}
            onUpdate={onUpdate}
            context={context}
          />
        )}
        {isReviewStep && (
          <Stack gap={3}>
            <ReviewStep
              profileOrder={preview.data?.profileOrder}
              isLoading={
                preview.isPending || (!preview.data && !preview.isError)
              }
            />
            {preview.isError && (
              <Stack gap={1.5} alignItems="center">
                <Banner
                  color="error"
                  Icon={<AlertCircle />}
                  message={t('wizard.errors.preview')}
                />
                <Button variant="outlined" onClick={handleRetry}>
                  {t('wizard.nav.retry')}
                </Button>
              </Stack>
            )}
            {pdfState === 'loading' && (
              <Stack alignItems="center" gap={1.5} sx={{ py: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="textSm" sx={{ color: 'text.secondary' }}>
                  {t('wizard.document.generating')}
                </Typography>
              </Stack>
            )}
            {pdfState === 'error' && !preview.isError && (
              <Stack gap={1.5} alignItems="center">
                <Banner
                  color="error"
                  Icon={<AlertCircle />}
                  message={t('wizard.errors.pdf')}
                />
                <Button variant="outlined" onClick={handleRetry}>
                  {t('wizard.nav.retry')}
                </Button>
              </Stack>
            )}
            {pdf && pdfState === 'idle' && (
              <Box
                component="iframe"
                src={pdf.url}
                title={t('wizard.document.title')}
                sx={{
                  display: 'block',
                  width: '100%',
                  // El informe de wio.pdf es A4 vertical: el visor toma la
                  // proporción de una hoja (210×297) para que la página se vea
                  // entera y a tamaño de lectura, no por una rendija. El
                  // mínimo cubre los anchos muy estrechos, donde el alto que
                  // sale de la proporción se queda corto.
                  aspectRatio: '210 / 297',
                  minHeight: 520,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              />
            )}
            {createMutation.isError && (
              <Banner
                color="error"
                Icon={<AlertCircle />}
                message={t('wizard.errors.submit')}
              />
            )}
          </Stack>
        )}
      </CardContent>
      <Divider sx={{ flexShrink: 0 }} />
      <CardActions sx={{ justifyContent: 'stretch' }}>
        <Button
          variant="outlined"
          onClick={wizard.goBack}
          disabled={wizard.isFirst || createMutation.isPending}
          sx={{ flexGrow: 1 }}
        >
          {t('wizard.nav.back')}
        </Button>
        {wizard.isLast ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !canAdvance ||
              !pdf ||
              pdfState === 'loading' ||
              preview.isPending ||
              createMutation.isPending
            }
            sx={{ flexGrow: 1 }}
          >
            {createMutation.isPending
              ? t('wizard.nav.submitting')
              : t('wizard.nav.submit')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={wizard.goNext}
            disabled={!canAdvance}
            sx={{ flexGrow: 1 }}
          >
            {t('wizard.nav.next')}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

const Loading = () => {
  const { t } = useTranslation();

  return (
    <Stack alignItems="center" gap={2} sx={{ py: 4 }}>
      <CircularProgress size={24} />
      <Typography variant="textSm" sx={{ color: 'text.secondary' }}>
        {t('wizard.loading')}
      </Typography>
    </Stack>
  );
};

/**
 * Punto de entrada: trae del API los textos del cuestionario, los inyecta en i18n y
 * monta el wizard del test que el propio API dice que aplica.
 *
 * Qué test se carga sale del claim del token: cada compañía contesta el suyo. El
 * contenido se sigue pidiendo primero porque de él salen los textos y la versión, y
 * sin ellos no se puede pintar nada. Si el EP falla, no hay contenido embebido de
 * respaldo.
 */
export const SuitabilityWizard = () => {
  const { clientId = '' } = useParams<{ clientId: string }>();
  const { companyId } = useEmbedAuth();
  const { t, i18n } = useTranslation();

  // Pedimos el idioma del navegador (sin la región: 'es-ES' -> 'es').
  // getQuestionnaireContent cae a 'es' si el EP no tiene ese idioma (404).
  const language = (i18n.language || 'es').split('-')[0];
  const contentQuery = useQuestionnaireContent(language);

  // La versión sale del contenido; el test, del claim. Los dos son derivables y no
  // necesitan estado propio.
  const version = contentQuery.data?.version ?? null;
  const test = companyId ? resolveCurrentTest(companyId) : undefined;

  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    if (contentReady || !contentQuery.data) return;

    injectQuestionnaireContent(
      i18n,
      i18n.language,
      contentQuery.data.content as InjectableContent,
    );
    setContentReady(true);
  }, [contentQuery.data, contentReady, i18n]);

  if (!clientId) {
    return (
      <Banner
        color="error"
        Icon={<AlertCircle />}
        message={t('wizard.errors.noClient')}
      />
    );
  }

  // Sin fallback embebido: si el EP de contenido falla, no podemos pintar el test.
  if (contentQuery.isError) {
    return (
      <Banner
        color="error"
        Icon={<AlertCircle />}
        message={t('wizard.errors.content')}
      />
    );
  }

  // El companyId llega tras el handshake, y el contenido después. Se espera a los dos
  // aunque el test ya se sepa por el claim: sin esta espera el banner de «no hay test»
  // parpadearía en cada carga lenta.
  if (!companyId || !contentReady || !version) {
    return <Loading />;
  }

  // Hay contenido pero este front no conoce el módulo de esa compañía: es un
  // despliegue a medias. Una compañía SIN test no llega aquí — su contenido responde
  // 404 y se para antes, en la guarda de arriba.
  if (!test) {
    return (
      <Banner
        color="error"
        Icon={<AlertCircle />}
        message={t('wizard.errors.noTest')}
      />
    );
  }

  return (
    <WizardRunner
      test={test}
      clientId={clientId}
      version={version}
      language={language}
    />
  );
};
